import { type AttachmentConfiguration, type PdfFormat, type TableAttachment } from '../../types/attachment';
import { scheduledSendExportFormats } from 'common/downloadLinks';
import { I18n } from 'common/i18n';
import { ATTACHMENT_ICONS } from '../../constants';
import { isStory } from 'common/views/view_types';
import { type View } from 'common/types/view';
import { isEmpty, isUndefined } from 'lodash';
import { type Hierarchy, type Vif } from 'common/visualizations/vif';
import SoqlDataProvider from 'common/visualizations/dataProviders/SoqlDataProvider';
import {
  getAllColumns,
  getSelectedColumns,
} from 'common/visualizations/views/BaseVisualization/SourceDataOptions';
import {
  getParameterOverrides,
  isTablePrintingImprovementGroupExpansionEnabled,
  isTablePrintingImprovementTextSizeSliderEnabled,
} from 'common/visualizations/helpers/VifSelectors';
import { type ViewColumn } from 'common/tests/dataMimics/View/types';
import { type QueryStringObject } from 'common/components/ExportModal';
import { PRINT_PREVIEW_FONT_SLIDER_CONFIG } from 'common/components/PdfPreviewModal/templates/section/defaultStyles';
import { isPivotFeatureFlagOn } from 'common/visualizations/helpers/PivotHelpers';

const scope = 'shared.components.schedule_send_report.modal.attachment_section';

export const getTableOptions = (tableList: TableAttachment[] | void) => {
  const tableAttachmentsList =
    tableList?.map((table: TableAttachment) => {
      return {
        label: table.title,
        value: table.componentUuid,
        leadingIcon: ATTACHMENT_ICONS['table_export'],
        leadingIconType: 'component',
        tooltip: { text: table.title },
      };
    }) ?? [];

  const fullReport = {
    label: I18n.t('full_report', { scope }),
    value: 'full-report',
    leadingIcon: ATTACHMENT_ICONS['full_report'],
    leadingIconType: 'component',
    tooltip: { text: I18n.t('full_report', { scope }) },
  };

  return [fullReport, ...tableAttachmentsList];
};

export const getExportFormatOptions = (attachmentConfig: AttachmentConfiguration) => {
  const isTableSupportedForPdfExport = attachmentConfig.isTableSupportedForPdfExport;

  // Since isTableSupportedForPdfExport can be undefined, we only want to disable the PDF option when it's explicitly false
  // and the attachment type is table_export.
  // In all other cases (true or undefined), we want to keep the PDF option enabled.
  const isPdfOptionDisabled =
    attachmentConfig.type === 'table_export' && isTableSupportedForPdfExport === false;
  return scheduledSendExportFormats.map((exportFormat) => {
    const disabled = isPdfOptionDisabled && exportFormat === 'pdf';
    const tooltip = I18n.t('exceeds_row_limit', { scope });
    return {
      label: I18n.t(`shared.explore_grid.export_dataset_modal.${exportFormat}`),
      value: exportFormat,
      disabled: disabled,
      tooltip: disabled ? { text: tooltip } : undefined,
    };
  });
};

export const sortComponents = (components: any[]) => {
  return components.sort((a, b) => {
    const ax = a.layout?.x;
    const ay = a.layout?.y;
    const bx = b.layout?.x;
    const by = b.layout?.y;

    //Check for invalid layout values
    const aInvalid =
      isUndefined(a.layout) || isUndefined(ax) || isUndefined(ay) || ax === null || ay === null;
    const bInvalid =
      isUndefined(b.layout) || isUndefined(bx) || isUndefined(by) || bx === null || by === null;

    if (aInvalid && !bInvalid) {
      return 1;
    }
    if (!aInvalid && bInvalid) {
      return -1;
    }
    if (aInvalid && bInvalid) {
      return 0;
    }

    //Sort Top -> Bottom
    if (ay !== by) {
      return (ay as number) - (by as number);
    }

    //Sort Left -> Right
    return (ax as number) - (bx as number);
  });
};

export const buildDefaultTableName = (tableCount: number) => {
  return `Table ${tableCount}`;
};

export const tablesFromStoryData = (view: View) => {
  if (isStory(view) && !isUndefined(window.STORY_DATA)) {
    let tableCount = 0;
    const isFlexibleStory = window.STORY_DATA.isFlexibleStory;
    const tablesComponents = window.STORY_DATA.blocks
      .map((block: any) => {
        const components = isFlexibleStory ? sortComponents(block.components) : block.components;

        return components.map((component: any) => {
          if (component.type === 'socrata.visualization.agTable') {
            tableCount = tableCount + 1;
            const tableVif = component.value.vif;
            const title = isEmpty(component.value.vif.title)
              ? buildDefaultTableName(tableCount)
              : component.value.vif.title;
            const hierarchies = component.value.vif.series[0].dataSource.hierarchies as Hierarchy[];
            const isWithGrouping = hierarchies?.some((hierarchy) => {
              const isPivotMode = isPivotFeatureFlagOn() && (hierarchy?.pivotMode ?? false);
              const groupedCount =
                hierarchy?.columnConfigurations?.filter((config) => config.isGrouping).length ?? 0;
              return isPivotMode ? groupedCount > 1 : groupedCount > 0;
            });

            // there should always be a componentUuid but putting a fallback just in case
            const componentUuid = component.componentUuid || `table-number-${tableCount}`;

            return {
              componentUuid: componentUuid,
              title: title,
              withGrouping: isWithGrouping === undefined ? false : isWithGrouping,
              withMultipleHierarchy: hierarchies?.length > 1,
              tableVif: tableVif,
            };
          }
        });
      })
      .flat()
      .filter((tableObj: TableAttachment) => tableObj !== undefined) as TableAttachment[];

    return tablesComponents;
  }
};

export const generateDefaultPdfConfig = (isTableWithGrouping: boolean): PdfFormat => {
  return {
    pageSize: 'Letter',
    orientation: 'landscape',
    columnWidth: isTablePrintingImprovementTextSizeSliderEnabled() ? 'inherit' : 'bestfit',
    textOverflow: 'linebreak',
    ...(isTableWithGrouping && { pageBreak: 'standard' }),
    ...(isTablePrintingImprovementTextSizeSliderEnabled() && {
      fontSize: PRINT_PREVIEW_FONT_SLIDER_CONFIG.DEFAULT,
    }),
    ...(isTableWithGrouping &&
      isTablePrintingImprovementGroupExpansionEnabled() && {
        groupExpansion: 'expanded' as const,
      }),
  };
};

export const joinTableNames = (tables: string[]) => {
  if (tables.length === 0) {
    return '';
  }
  if (tables.length === 1) {
    return tables[0];
  }
  if (tables.length === 2) {
    return tables.join(` ${I18n.t('and', { scope })} `);
  }
  return tables.slice(0, -1).join(', ') + `, ${I18n.t('and', { scope })} ` + tables[tables.length - 1];
};

const constructQueryObject = (
  vif: Vif,
  columns: ViewColumn[],
  whereClause: string,
  seriesIndex?: number,
): QueryStringObject => {
  const selectedColumns = getSelectedColumns(vif, seriesIndex, columns);
  return {
    selectClause: selectedColumns ? `select ${selectedColumns.join(', ')}` : 'select *',
    whereClause: whereClause ? `where ${whereClause}` : '',
  };
};

export const getHostname = () => window.location.hostname;

export const getRowCountForTable = async (
  selectedTable: TableAttachment,
  rowCountCache: Record<string, number>,
  setRowCountCache: React.Dispatch<React.SetStateAction<Record<string, number>>>,
  filterWhereClauses?: Record<string, string | null>,
): Promise<number> => {
  const componentUuid = selectedTable.componentUuid;

  // Check cache first
  if (rowCountCache[componentUuid] !== undefined) {
    return rowCountCache[componentUuid];
  }

  const datasetUid = selectedTable.tableVif.series[0].dataSource.datasetUid as string;
  const clientContextVariables = getParameterOverrides(selectedTable.tableVif);
  const domain = getHostname();

  // Get the stored row count for this table
  const storedFilterWhereClause = filterWhereClauses ? filterWhereClauses[componentUuid] : null;

  const soqlDataProvider = new SoqlDataProvider({ domain, datasetUid, clientContextVariables }, true);
  const queryStringClause = constructQueryObject(
    selectedTable.tableVif,
    getAllColumns(selectedTable.tableVif, 0),
    storedFilterWhereClause || '',
    0,
  );

  const queryString = `${queryStringClause.selectClause} ${queryStringClause.whereClause}`;

  try {
    const rowCount = await soqlDataProvider.getRowCountForQuery(queryString);

    // Cache the result before returning
    setRowCountCache((prev) => ({
      ...prev,
      [componentUuid]: rowCount,
    }));

    return rowCount;
  } catch (error) {
    console.error('Error fetching row count:', error);
    return 0;
  }
};
